import moment from 'moment';
import { useForm, Controller, FormProvider } from 'react-hook-form';
import { PropTypes } from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { injectIntl } from 'react-intl';
import { IoCameraOutline, IoCloudUploadOutline, IoImagesOutline } from 'react-icons/io5';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { compose } from 'redux';
import { DrawerControls } from '../../../core/components/Drawer';
import ButtonWithLoader from '../../../core/components/ButtonWithLoader';
import ModalDialog from '../../../core/components/Modal';
import SuggestedTextPicker from '../../../core/components/SuggestedTextPicker';
import * as toastMessages from '../../../core/toastMessages';
import { ReclamationStatus, ReclamationType, ReclamationWashType } from '../models';
import { selectReclamationDamageOptions } from '../reducer';
import { fetchCarMakes, fetchCarModels } from '../../../core/actions';
import { selectCarMakes, selectCarModels } from '../../../store/carModelReducer';
import { map } from 'lodash';
import './ReclamationForm.scss';
import Stepper from '../../../core/components/Stepper';
import ClaimWizardReviewSection from './claimWizard/ClaimWizardReviewSection';
import {
  CLAIM_EVENT_OPTIONS,
  CLAIM_WIZARD_STEPS,
  INVOLVED_OPTIONS,
  RELATIONSHIP_OPTIONS,
  getInitialClaimWizardStep,
} from './claimWizard/claimWizardConstants';

const normalizeClaimDescription = (description) => {
  if (typeof description === 'string') return description;
  if (!description || typeof description !== 'object') return '';

  const textValue = description.value || description.text || description.description;
  return typeof textValue === 'string' ? textValue : '';
};

const getPhotoFileKey = (file) => [file.name, file.size, file.type, file.lastModified].join(':');

export const ClaimWizardForm = (props) => {
  // eslint-disable-next-line
  const { type, sites, isNew, completeMode } = props;
  const formatMessage = (id, values) => props.intl.formatMessage({ id }, values);
  const getEventTitle = (option) => (option ? formatMessage(option.titleId) : '');
  const claimWizardStepLabels = CLAIM_WIZARD_STEPS.map((step) => formatMessage(step.id));
  const defaultWashType =
    props?.initialValues && typeof props.initialValues.issueWashType === 'number'
      ? props.initialValues.issueWashType
      : ReclamationWashType.Exterior;
  const buildFormValues = (initialValues) => {
    const vehicleDetails = (initialValues && initialValues.vehicleDetails) || {};
    return {
      description: normalizeClaimDescription(initialValues?.description),
      issueDate: initialValues?.issueDate || null,
      issueSiteId: initialValues?.issueSiteId || initialValues?.issueSite?.id || null,
      issueWashType:
        typeof initialValues?.issueWashType === 'number'
          ? initialValues.issueWashType
          : ReclamationWashType.Exterior,
      employeeIds: initialValues?.employeeIds || [],
      customerName: initialValues?.customerName || '',
      customerEmail: initialValues?.customerEmail || '',
      customerPhone: initialValues?.customerPhone || '',
      vehicleDetails: {
        make: vehicleDetails.make || '',
        model: vehicleDetails.model || '',
        year: vehicleDetails.year ?? null,
        color: vehicleDetails.color || '',
        vin: vehicleDetails.vin || '',
        plate: vehicleDetails.plate || '',
        membershipNumber: vehicleDetails.membershipNumber || '',
      },
      claimDamagePartId: initialValues?.claimDamagePartId ?? null,
      claimDamageAreaId: initialValues?.claimDamageAreaId ?? null,
      incidentDetails: initialValues?.incidentDetails || {},
      status: initialValues?.status ?? null,
    };
  };
  const rhfDefaultValues = buildFormValues(props?.initialValues);
  const methods = useForm({ defaultValues: rhfDefaultValues });
  const { control, watch, register, handleSubmit, reset, setValue } = methods;
  const [isEditing, setIsEditing] = React.useState(false);
  const [claimWizardStep, setClaimWizardStep] = useState(
    completeMode ? getInitialClaimWizardStep(rhfDefaultValues, props.carParts || []) : 0
  );
  const [claimWizardError, setClaimWizardError] = useState('');
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [isPhotoSourceOpen, setIsPhotoSourceOpen] = useState(false);
  const cameraInputRef = useRef(null);
  const photoUploadInputRef = useRef(null);
  const [showAdditionalContact, setShowAdditionalContact] = useState(false);
  const selectedPhotoPreviews = React.useMemo(
    () =>
      selectedPhotos.map((file) => ({
        file,
        url: typeof URL.createObjectURL === 'function' ? URL.createObjectURL(file) : '',
      })),
    [selectedPhotos]
  );

  const addSelectedPhotos = (event) => {
    const addedPhotos = Array.from(event.target.files || []);
    setSelectedPhotos((currentPhotos) => {
      const selectedPhotoKeys = new Set(currentPhotos.map(getPhotoFileKey));
      const uniqueAddedPhotos = addedPhotos.filter((file) => {
        const fileKey = getPhotoFileKey(file);
        if (selectedPhotoKeys.has(fileKey)) return false;
        selectedPhotoKeys.add(fileKey);
        return true;
      });
      return [...currentPhotos, ...uniqueAddedPhotos];
    });
    event.target.value = '';
  };

  const selectPhotoSource = (inputRef) => {
    setIsPhotoSourceOpen(false);
    inputRef.current?.click();
  };

  useEffect(
    () => () => {
      selectedPhotoPreviews.forEach(({ url }) => {
        if (url && typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(url);
      });
    },
    [selectedPhotoPreviews]
  );

  // Reset form when initialValues change (e.g., when editing a different row)
  useEffect(() => {
    if (props.initialValues && !isEditing) {
      const washType =
        typeof props.initialValues.issueWashType === 'number'
          ? props.initialValues.issueWashType
          : ReclamationWashType.Exterior;
      const nextValues = buildFormValues({ ...props.initialValues, issueWashType: washType });
      reset(nextValues);
      if (completeMode) {
        setClaimWizardStep(getInitialClaimWizardStep(nextValues, props.carParts || []));
      }
    }
  }, [props.initialValues, reset, isEditing, completeMode, props.carParts]);

  useEffect(() => {
    if (props.change) props.change('issueWashType', defaultWashType);
    const subscription = watch((value, { name }) => {
      if (name && !isEditing) {
        setIsEditing(true);
      }
      if (name === 'issueWashType' && props.change) {
        props.change('issueWashType', value.issueWashType);
      }
    });
    return () => {
      if (subscription && subscription.unsubscribe) subscription.unsubscribe();
    };
  }, [watch, props.change, isEditing]);

  const isClaimCreateWizard = (isNew || completeMode) && type === ReclamationType.Claim;

  useEffect(() => {
    if (!isClaimCreateWizard) return undefined;
    document.body.classList.add('claim-wizard-body');
    return () => {
      document.body.classList.remove('claim-wizard-body');
    };
  }, [isClaimCreateWizard]);

  // Car make/model wizard helpers
  const selectedVehicleMake = watch('vehicleDetails.make');
  const selectedMakeItem = React.useMemo(
    () => (props.carMakes || []).find((m) => m.make === selectedVehicleMake),
    [props.carMakes, selectedVehicleMake]
  );
  const selectedMakeId = selectedMakeItem ? selectedMakeItem.makeId : null;
  const carModelOptions = selectedMakeId && props.carModels ? props.carModels[selectedMakeId] : [];

  useEffect(() => {
    if (props.fetchCarMakes) {
      props.fetchCarMakes();
    }
  }, [props.fetchCarMakes]);

  useEffect(() => {
    if (selectedMakeId && props.fetchCarModels) {
      props.fetchCarModels(selectedMakeId);
      const currentModel = watch('vehicleDetails.model');
      const modelExists = (carModelOptions || []).some((m) => m.model === currentModel);
      if (currentModel && !modelExists) {
        setValue('vehicleDetails.model', '', { shouldDirty: true });
      }
    }
  }, [selectedMakeId, props.fetchCarModels, setValue, watch]);

  // Derive affectedArea from selected car part via RHF
  const selectedCarPartId = watch('claimDamagePartId');
  const affectedArea = (() => {
    const parts = props.carParts || [];
    const part = parts.find((p) => String(p.id) === String(selectedCarPartId));
    return part && Array.isArray(part.options) ? part.options : undefined;
  })();

  useEffect(() => {
    if (isClaimCreateWizard && selectedCarPartId) {
      const parts = props.carParts || [];
      const part = parts.find((p) => String(p.id) === String(selectedCarPartId));
      const options = part && Array.isArray(part.options) ? map(part.options, 'id') : [];
      const currentAreaId = watch('claimDamageAreaId');
      if (options.length && !options.includes(currentAreaId)) {
        setValue('claimDamageAreaId', options[0] || null, { shouldDirty: true });
      }
    }
  }, [selectedCarPartId, props.carParts, isClaimCreateWizard, setValue, watch]);

  const selectedEventType = watch('incidentDetails.claimChecklist.eventType');
  const selectedEvent = CLAIM_EVENT_OPTIONS.find((option) => option.value === selectedEventType);
  const selectedEventTitle = getEventTitle(selectedEvent);
  const watchedValues = watch();
  const washTypeLabels = {
    [ReclamationWashType.Exterior]: formatMessage('exterior'),
    [ReclamationWashType.Interior]: formatMessage('interior'),
    [ReclamationWashType.Both]: formatMessage('both'),
  };
  const selectedWashTypeLabel = washTypeLabels[watchedValues.issueWashType];
  const selectedEmployeeId = watchedValues?.employeeIds?.[0]?.employeeId;
  const selectedEmployee = (props.employees || []).find(
    (employee) => String(employee.id) === String(selectedEmployeeId)
  );
  const selectedInvolvedOption = INVOLVED_OPTIONS.find(
    (option) => option.value === watchedValues?.incidentDetails?.claimChecklist?.involved
  );

  useEffect(() => {
    if (watch('employeeIds')?.length || !props.currentUser || !props.employees?.length) return;

    const loggedInEmployee = props.employees.find(
      (employee) => String(employee.coreUserId) === String(props.currentUser.id)
    );
    if (loggedInEmployee) {
      setValue('employeeIds', [{ employeeId: loggedInEmployee.id }]);
    }
  }, [props.currentUser, props.employees, setValue, watch]);

  const getIssueDateFromEvidence = (formValues) => {
    const eventDate = formValues?.incidentDetails?.claimChecklist?.eventDate;
    const eventTime = formValues?.incidentDetails?.claimChecklist?.eventTime;
    if (!eventDate) return formValues.issueDate;

    const dateTime = eventTime ? `${eventDate} ${eventTime}` : eventDate;
    const dateFormat = eventTime ? 'YYYY-MM-DD HH:mm' : 'YYYY-MM-DD';
    const parsed = moment(dateTime, dateFormat);
    return parsed.isValid() ? parsed.toISOString() : formValues.issueDate;
  };

  const buildSubmitPayload = (formValues, finishLater = false) => {
    const payload = {
      ...props.initialValues,
      ...formValues,
      employeeIds: formValues.employeeIds,
      issueDate: getIssueDateFromEvidence(formValues),
    };

    if (isNew) {
      delete payload.id;
    }

    payload.incidentDetails = {
      ...(payload.incidentDetails || {}),
      claimChecklist: {
        ...(payload.incidentDetails?.claimChecklist || {}),
        photoNames: selectedPhotos.map((file) => file.name),
      },
    };

    if (payload && payload.vehicleDetails) {
      const y = payload.vehicleDetails.year;
      if (y === '' || y === undefined) {
        payload.vehicleDetails.year = null;
      } else if (y !== null) {
        const n = Number(y);
        payload.vehicleDetails.year = Number.isNaN(n) ? null : parseInt(String(n), 10);
      }
    }
    if (
      payload.issueSiteId !== '' &&
      payload.issueSiteId !== null &&
      payload.issueSiteId !== undefined
    ) {
      payload.issueSiteId = Number(payload.issueSiteId);
    }
    if ((isNew || completeMode) && type === ReclamationType.Claim) {
      payload.status = finishLater ? ReclamationStatus.Incomplete : ReclamationStatus.Open;
    }

    // Ensure IDs are integers (select values may come through as strings)
    const castToIntOrNull = (val) => {
      if (val === '' || val === undefined || val === null) return null;
      const n = Number(val);
      return Number.isFinite(n) ? n : null;
    };
    payload.claimDamagePartId = castToIntOrNull(payload.claimDamagePartId);
    payload.claimDamageAreaId = castToIntOrNull(payload.claimDamageAreaId);
    payload.description = normalizeClaimDescription(payload.description);

    return payload;
  };

  const validateClaimWizardStep = (stepIndex = claimWizardStep) => {
    if (stepIndex === 0 && !watch('incidentDetails.claimChecklist.eventType')) {
      return formatMessage('claimWizardValidationChooseEventType');
    }
    if (stepIndex === 1) {
      if (!String(watch('description') || '').trim()) {
        return formatMessage('claimWizardValidationAddIncidentDescription');
      }
      if (!watch('claimDamagePartId')) {
        return formatMessage('claimWizardValidationSelectCarPart');
      }
      const areaOptions = affectedArea;
      if (areaOptions !== undefined && areaOptions.length && !watch('claimDamageAreaId')) {
        return formatMessage('claimWizardValidationSelectAffectedArea');
      }
      if (!watch('incidentDetails.claimChecklist.involved')) {
        return formatMessage('claimWizardValidationSelectInvolved');
      }
    }
    if (stepIndex === 2) {
      if (!watch('incidentDetails.claimChecklist.eventDate')) {
        return formatMessage('claimWizardValidationAddEventDate');
      }
      if (!watch('issueSiteId')) {
        return formatMessage('claimWizardValidationSelectSiteLocation');
      }
    }
    if (stepIndex === 3) {
      if (!watch('incidentDetails.claimChecklist.primaryContactRelationship')) {
        return formatMessage('claimWizardValidationSelectRelationship');
      }
      if (!String(watch('customerName') || '').trim()) {
        return formatMessage('claimWizardValidationAddContactName');
      }
      if (
        !String(watch('customerPhone') || '').trim() &&
        !String(watch('customerEmail') || '').trim()
      ) {
        return formatMessage('claimWizardValidationAddPhoneOrEmail');
      }
    }
    return '';
  };

  const goToClaimWizardStep = (nextStep) => {
    const error = nextStep > claimWizardStep ? validateClaimWizardStep() : '';
    if (error) {
      setClaimWizardError(error);
      return;
    }
    setClaimWizardError('');
    setClaimWizardStep(Math.max(0, Math.min(CLAIM_WIZARD_STEPS.length - 1, nextStep)));
  };

  const jumpToClaimWizardStep = (nextStep) => {
    setClaimWizardError('');
    setClaimWizardStep(Math.max(0, Math.min(CLAIM_WIZARD_STEPS.length - 1, nextStep)));
  };

  const saveClaimForLater = handleSubmit((formValues) => {
    const description = normalizeClaimDescription(formValues.description).trim();
    const hasName = String(formValues.customerName || '').trim();
    const hasContact = String(formValues.customerPhone || formValues.customerEmail || '').trim();
    const hasIssueDate = getIssueDateFromEvidence(formValues);
    if (!description) {
      const message = formatMessage('claimWizardValidationAddIncidentDescription');
      setClaimWizardError(message);
      toastMessages.generateToastr('warning', formatMessage('claimWizardMoreInfoNeeded'), message);
      setClaimWizardStep(1);
      return;
    }
    if (!hasIssueDate) {
      const message = formatMessage('claimWizardSaveLaterMissingEventDate');
      setClaimWizardError(message);
      toastMessages.generateToastr('warning', formatMessage('claimWizardMoreInfoNeeded'), message);
      setClaimWizardStep(2);
      return;
    }
    if (!hasName || !hasContact) {
      const message = formatMessage('claimWizardSaveLaterMissingContact');
      setClaimWizardError(message);
      toastMessages.generateToastr('warning', formatMessage('claimWizardMoreInfoNeeded'), message);
      setClaimWizardStep(3);
      return;
    }
    setClaimWizardError('');
    props.onSubmit(buildSubmitPayload(formValues, true), selectedPhotos);
  });

  const submitClaimWizard = handleSubmit((formValues) => {
    const firstInvalidStep = [0, 1, 2, 3].find((stepIndex) => validateClaimWizardStep(stepIndex));
    if (firstInvalidStep !== undefined) {
      setClaimWizardStep(firstInvalidStep);
      setClaimWizardError(validateClaimWizardStep(firstInvalidStep));
      return;
    }
    setClaimWizardError('');
    props.onSubmit(buildSubmitPayload(formValues), selectedPhotos);
  });

  if (isClaimCreateWizard) {
    const renderBooleanChoice = (name, label) => {
      const value = watch(name);
      return (
        <div className="claim-wizard-field">
          <div className="claim-wizard-label">{label}</div>
          <div className="claim-wizard-segmented">
            <button
              type="button"
              className={value === true ? 'active' : ''}
              onClick={() => setValue(name, true, { shouldDirty: true })}
            >
              {formatMessage('yes')}
            </button>
            <button
              type="button"
              className={value === false ? 'active' : ''}
              onClick={() => setValue(name, false, { shouldDirty: true })}
            >
              {formatMessage('no')}
            </button>
          </div>
        </div>
      );
    };

    const renderStepContent = () => {
      switch (claimWizardStep) {
        case 0:
          return (
            <>
              <h1>{formatMessage('claimWizardEventHeading')}</h1>
              <p className="claim-wizard-subtitle">{formatMessage('claimWizardEventSubtitle')}</p>
              <div className="claim-wizard-event-grid">
                {CLAIM_EVENT_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={`claim-wizard-event-card${
                      selectedEventType === option.value ? ' selected' : ''
                    }`}
                    onClick={() =>
                      setValue('incidentDetails.claimChecklist.eventType', option.value, {
                        shouldDirty: true,
                      })
                    }
                  >
                    <span className="claim-wizard-event-title">{getEventTitle(option)}</span>
                    <span className="claim-wizard-event-description">
                      {formatMessage(option.descriptionId)}
                    </span>
                  </button>
                ))}
              </div>
            </>
          );
        case 1:
          return (
            <>
              <h1>{selectedEventTitle || formatMessage('claimWizardDetailsFallbackHeading')}</h1>
              <p className="claim-wizard-subtitle">{formatMessage('claimWizardDetailsSubtitle')}</p>
              <div className="claim-wizard-field">
                <label htmlFor="description">
                  {formatMessage('claimWizardIncidentDescription')} *
                </label>
                <textarea
                  {...register('description', { required: true })}
                  className="form-control"
                  id="description"
                  placeholder={formatMessage('claimWizardIncidentDescriptionPlaceholder')}
                  maxLength="255"
                  data-testid="description-reclaimation-form"
                  required
                />
              </div>
              <div className="claim-wizard-two-column">
                <div className="claim-wizard-field">
                  <label htmlFor="claimWizardCarPart">
                    {formatMessage('claimWizardCarPart')} *
                  </label>
                  <select
                    id="claimWizardCarPart"
                    className="form-control"
                    {...register('claimDamagePartId')}
                  >
                    <option value="">{formatMessage('select')}...</option>
                    {(props.carParts || []).map((part) => (
                      <option key={part.id} value={part.id}>
                        {part.value}
                      </option>
                    ))}
                  </select>
                </div>
                {affectedArea !== undefined && (
                  <div className="claim-wizard-field">
                    <label htmlFor="claimWizardAffectedArea">
                      {formatMessage('claimWizardAffectedArea')} *
                    </label>
                    <select
                      id="claimWizardAffectedArea"
                      className="form-control"
                      {...register('claimDamageAreaId')}
                    >
                      <option value="">{formatMessage('select')}...</option>
                      {affectedArea.map((area) => (
                        <option key={area.id} value={area.id}>
                          {area.value}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              <div className="claim-wizard-field">
                <label htmlFor="claimWizardInvolved">
                  {formatMessage('claimWizardInvolved')} *
                </label>
                <select
                  id="claimWizardInvolved"
                  className="form-control"
                  {...register('incidentDetails.claimChecklist.involved')}
                >
                  <option value="">{formatMessage('select')}...</option>
                  {INVOLVED_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {formatMessage(option.labelId)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="claim-wizard-field">
                <label htmlFor="claimWizardEmployee">{formatMessage('employee')}</label>
                <select
                  id="claimWizardEmployee"
                  className="form-control"
                  value={selectedEmployeeId || ''}
                  onChange={(event) =>
                    setValue(
                      'employeeIds',
                      event.target.value ? [{ employeeId: Number(event.target.value) }] : [],
                      { shouldDirty: true }
                    )
                  }
                >
                  <option value="">{formatMessage('select')}...</option>
                  {(props.employees || []).map((employee) => (
                    <option key={employee.id} value={employee.id}>
                      {employee.firstName} {employee.lastName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="claim-wizard-field">
                <label htmlFor="claimWizardWashType">
                  {formatMessage('claimWizardWashType')} *
                </label>
                <select
                  id="claimWizardWashType"
                  className="form-control"
                  value={watchedValues.issueWashType ?? ''}
                  onChange={(event) =>
                    setValue('issueWashType', Number(event.target.value), { shouldDirty: true })
                  }
                  required
                >
                  <option value={ReclamationWashType.Exterior}>{formatMessage('exterior')}</option>
                  <option value={ReclamationWashType.Interior}>{formatMessage('interior')}</option>
                  <option value={ReclamationWashType.Both}>{formatMessage('both')}</option>
                </select>
              </div>
              {renderBooleanChoice(
                'incidentDetails.claimChecklist.anotherPartyInvolved',
                formatMessage('claimWizardAnotherPartyInvolved')
              )}
              {renderBooleanChoice(
                'incidentDetails.claimChecklist.policeReportFiled',
                formatMessage('claimWizardPoliceReportFiled')
              )}
              {renderBooleanChoice(
                'incidentDetails.claimChecklist.injuries',
                formatMessage('claimWizardInjuries')
              )}
            </>
          );
        case 2:
          return (
            <>
              <h1>{formatMessage('claimWizardEvidenceHeading')}</h1>
              <p className="claim-wizard-subtitle">
                {formatMessage('claimWizardEvidenceSubtitle')}
              </p>
              <div className="claim-wizard-two-column">
                <div className="claim-wizard-field">
                  <label htmlFor="claimWizardEventDate">
                    {formatMessage('claimWizardDateOfEvent')} *
                  </label>
                  <input
                    id="claimWizardEventDate"
                    type="date"
                    className="form-control"
                    {...register('incidentDetails.claimChecklist.eventDate')}
                  />
                </div>
                <div className="claim-wizard-field">
                  <label htmlFor="claimWizardEventTime">
                    {formatMessage('claimWizardApproxTime')}
                  </label>
                  <input
                    id="claimWizardEventTime"
                    type="time"
                    className="form-control"
                    {...register('incidentDetails.claimChecklist.eventTime')}
                  />
                </div>
              </div>
              <div className="claim-wizard-field">
                <label htmlFor="claimWizardIssueSite">
                  {formatMessage('claimWizardSiteLocation')} *
                </label>
                <select
                  id="claimWizardIssueSite"
                  className="form-control"
                  {...register('issueSiteId')}
                >
                  <option value="">{formatMessage('claimWizardSelectSite')}...</option>
                  {(sites || []).map((site) => (
                    <option key={site.id} value={site.id}>
                      {site.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="claim-wizard-field">
                <label>{formatMessage('photos')}</label>
                <button
                  type="button"
                  className="claim-wizard-upload"
                  onClick={() => setIsPhotoSourceOpen(true)}
                >
                  <IoImagesOutline aria-hidden="true" />
                  <span>{formatMessage('claimWizardAddPhotos')}</span>
                </button>
                <input
                  ref={cameraInputRef}
                  id="claimWizardCamera"
                  className="claim-wizard-photo-input"
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={addSelectedPhotos}
                />
                <input
                  ref={photoUploadInputRef}
                  id="claimWizardPhotos"
                  className="claim-wizard-photo-input"
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={addSelectedPhotos}
                />
                <ModalDialog
                  title={formatMessage('claimWizardAddPhotos')}
                  isOpen={isPhotoSourceOpen}
                  close={() => setIsPhotoSourceOpen(false)}
                  showFooter={false}
                >
                  <div className="claim-wizard-photo-source-actions">
                    <button
                      type="button"
                      className="button primary mobile-camera-option"
                      onClick={() => selectPhotoSource(cameraInputRef)}
                    >
                      <IoCameraOutline aria-hidden="true" />
                      <span>{formatMessage('claimWizardTakePhoto')}</span>
                    </button>
                    <button
                      type="button"
                      className="button"
                      onClick={() => selectPhotoSource(photoUploadInputRef)}
                    >
                      <IoCloudUploadOutline aria-hidden="true" />
                      <span>{formatMessage('claimWizardUploadPhotos')}</span>
                    </button>
                    <button
                      type="button"
                      className="button"
                      onClick={() => setIsPhotoSourceOpen(false)}
                    >
                      {formatMessage('cancel')}
                    </button>
                  </div>
                </ModalDialog>
                {selectedPhotos.length > 0 && (
                  <div className="claim-wizard-photo-list">
                    {selectedPhotoPreviews.map(({ file, url }) => (
                      <div className="claim-wizard-photo-card" key={getPhotoFileKey(file)}>
                        <img src={url} alt={file.name} />
                        <div className="claim-wizard-photo-card-footer">
                          <span title={file.name}>{file.name}</span>
                          <button
                            type="button"
                            onClick={() =>
                              setSelectedPhotos((currentPhotos) =>
                                currentPhotos.filter(
                                  (selectedFile) =>
                                    getPhotoFileKey(selectedFile) !== getPhotoFileKey(file)
                                )
                              )
                            }
                            aria-label={`Remove ${file.name}`}
                          >
                            ×
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          );
        case 3:
          return (
            <>
              <h1>{formatMessage('claimWizardContactHeading')}</h1>
              <p className="claim-wizard-subtitle">{formatMessage('claimWizardContactSubtitle')}</p>
              <div className="claim-wizard-contact-panel">
                <div className="claim-wizard-panel-title">
                  {formatMessage('claimWizardPrimaryContact')}
                </div>
                <div className="claim-wizard-field">
                  <label htmlFor="claimWizardRelationship">
                    {formatMessage('claimWizardRelationshipToClaim')} *
                  </label>
                  <select
                    id="claimWizardRelationship"
                    className="form-control"
                    {...register('incidentDetails.claimChecklist.primaryContactRelationship')}
                  >
                    <option value="">{formatMessage('select')}...</option>
                    {RELATIONSHIP_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {formatMessage(option.labelId)}
                      </option>
                    ))}
                  </select>
                  <small className="claim-wizard-field-helper">
                    {formatMessage('claimWizardRelationshipHelper')}
                  </small>
                </div>
                <div className="claim-wizard-field">
                  <label htmlFor="customerName">{formatMessage('claimWizardFullName')} *</label>
                  <input
                    className="form-control"
                    id="customerName"
                    placeholder={formatMessage('claimWizardNamePlaceholder')}
                    data-testid="customerName-reclaimation-form"
                    {...register('customerName')}
                  />
                </div>
                <div className="claim-wizard-two-column">
                  <div className="claim-wizard-field">
                    <label htmlFor="customerPhone">{formatMessage('phone')} *</label>
                    <input
                      className="form-control"
                      id="customerPhone"
                      placeholder={formatMessage('claimWizardPhonePlaceholder')}
                      data-testid="customerPhone-reclaimation-form"
                      {...register('customerPhone')}
                    />
                  </div>
                  <div className="claim-wizard-field">
                    <label htmlFor="customerEmail">{formatMessage('email')} *</label>
                    <input
                      className="form-control"
                      id="customerEmail"
                      placeholder={formatMessage('claimWizardEmailPlaceholder')}
                      data-testid="customerEmail-reclaimation-form"
                      {...register('customerEmail')}
                    />
                  </div>
                </div>
                <div className="claim-wizard-two-column">
                  <div className="claim-wizard-field">
                    <label htmlFor="vehicleMake">{formatMessage('make')}</label>
                    <Controller
                      name="vehicleDetails.make"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <SuggestedTextPicker
                          input={{ value: value || '', onChange }}
                          updateOnType={false}
                          items={props.carMakes || []}
                          renderSuggestion={(item) => (
                            <div className="suggestion">
                              <span>{item.make}</span>
                            </div>
                          )}
                          placeholder={formatMessage('make').toLowerCase()}
                          matchProperty="make"
                          intl={props.intl}
                        />
                      )}
                    />
                  </div>
                  <div className="claim-wizard-field">
                    <label htmlFor="vehicleModel">{formatMessage('model')}</label>
                    <Controller
                      name="vehicleDetails.model"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <SuggestedTextPicker
                          input={{ value: value || '', onChange }}
                          updateOnType
                          itemCallback={() => carModelOptions || []}
                          renderSuggestion={(item) => (
                            <div className="suggestion">
                              <span>{item.model}</span>
                            </div>
                          )}
                          placeholder={formatMessage('model').toLowerCase()}
                          matchProperty="model"
                          intl={props.intl}
                        />
                      )}
                    />
                  </div>
                </div>
                <div className="claim-wizard-two-column">
                  <div className="claim-wizard-field">
                    <label htmlFor="vehicleYear">{formatMessage('year')}</label>
                    <input
                      className="form-control"
                      id="vehicleYear"
                      placeholder={formatMessage('claimWizardVehicleYearPlaceholder')}
                      data-testid="vehicleYear-reclaimation-form"
                      {...register('vehicleDetails.year')}
                    />
                  </div>
                  <div className="claim-wizard-field">
                    <label htmlFor="vehicleColor">{formatMessage('color')}</label>
                    <input
                      className="form-control"
                      id="vehicleColor"
                      placeholder={formatMessage('claimWizardVehicleColorPlaceholder')}
                      data-testid="vehicleColor-reclaimation-form"
                      {...register('vehicleDetails.color')}
                    />
                  </div>
                </div>
                <div className="claim-wizard-two-column">
                  <div className="claim-wizard-field">
                    <label htmlFor="vehicleVin">{formatMessage('vin')}</label>
                    <input
                      className="form-control"
                      id="vehicleVin"
                      placeholder={formatMessage('claimWizardVinPlaceholder')}
                      data-testid="vehicleVin-reclaimation-form"
                      {...register('vehicleDetails.vin')}
                    />
                  </div>
                  <div className="claim-wizard-field">
                    <label htmlFor="vehiclePlate">{formatMessage('claimWizardLicensePlate')}</label>
                    <input
                      className="form-control"
                      id="vehiclePlate"
                      placeholder={formatMessage('claimWizardPlatePlaceholder')}
                      data-testid="vehiclePlate-reclaimation-form"
                      {...register('vehicleDetails.plate')}
                    />
                  </div>
                </div>
                <div className="claim-wizard-field">
                  <label htmlFor="vehicleMembershipNumber">
                    {formatMessage('claimWizardMembershipNumber')}
                  </label>
                  <input
                    className="form-control"
                    id="vehicleMembershipNumber"
                    placeholder={formatMessage('claimWizardMembershipPlaceholder')}
                    data-testid="vehicleMembershipNumber-reclaimation-form"
                    {...register('vehicleDetails.membershipNumber')}
                  />
                  <small className="form-text text-muted">
                    {formatMessage('claimWizardMembershipHelper')}
                  </small>
                </div>
              </div>
              {showAdditionalContact && (
                <div className="claim-wizard-contact-panel">
                  <div className="claim-wizard-panel-title">
                    {formatMessage('claimWizardAdditionalContact')}
                  </div>
                  <div className="claim-wizard-two-column">
                    <input
                      className="form-control"
                      placeholder={formatMessage('name')}
                      {...register('incidentDetails.claimChecklist.additionalContact.name')}
                    />
                    <input
                      className="form-control"
                      placeholder={formatMessage('claimWizardPhoneOrEmail')}
                      {...register('incidentDetails.claimChecklist.additionalContact.contact')}
                    />
                  </div>
                </div>
              )}
              {!showAdditionalContact && (
                <button
                  type="button"
                  className="button claim-wizard-add-contact"
                  onClick={() => setShowAdditionalContact(true)}
                >
                  {formatMessage('claimWizardAddAnotherContact')}
                </button>
              )}
            </>
          );
        case 4:
        default:
          return (
            <div className="claim-wizard-review">
              <h1>{formatMessage('claimWizardReviewHeading')}</h1>
              <p className="claim-wizard-subtitle">{formatMessage('claimWizardReviewSubtitle')}</p>
              {[
                {
                  label: formatMessage('claimWizardReviewEvent'),
                  step: 0,
                  rows: [[formatMessage('type'), selectedEventTitle || formatMessage('none')]],
                },
                {
                  label: formatMessage('claimWizardReviewDetails'),
                  step: 1,
                  rows: [
                    [
                      formatMessage('claimWizardCarPart'),
                      (props.carParts || []).find(
                        (p) => String(p.id) === String(watchedValues?.claimDamagePartId)
                      )?.value || formatMessage('none'),
                    ],
                    ...(affectedArea !== undefined
                      ? [
                          [
                            formatMessage('claimWizardAffectedArea'),
                            affectedArea.find(
                              (a) => String(a.id) === String(watchedValues?.claimDamageAreaId)
                            )?.value || formatMessage('none'),
                          ],
                        ]
                      : []),
                    [
                      formatMessage('claimWizardInvolved'),
                      selectedInvolvedOption
                        ? formatMessage(selectedInvolvedOption.labelId)
                        : formatMessage('none'),
                    ],
                    [
                      formatMessage('employee'),
                      selectedEmployee
                        ? `${selectedEmployee.firstName} ${selectedEmployee.lastName}`
                        : formatMessage('none'),
                    ],
                    [
                      formatMessage('claimWizardWashType'),
                      selectedWashTypeLabel || formatMessage('none'),
                    ],
                  ],
                },
                {
                  label: formatMessage('claimWizardReviewEvidence'),
                  step: 2,
                  rows: [
                    [
                      formatMessage('date'),
                      watchedValues?.incidentDetails?.claimChecklist?.eventDate ||
                        formatMessage('none'),
                    ],
                    [
                      formatMessage('photos'),
                      selectedPhotos.length ? (
                        <div className="claim-wizard-review-photos">
                          {selectedPhotoPreviews.map(({ file, url }) => (
                            <img
                              key={getPhotoFileKey(file)}
                              src={url}
                              alt={file.name}
                              title={file.name}
                            />
                          ))}
                        </div>
                      ) : (
                        formatMessage('none')
                      ),
                    ],
                  ],
                },
                {
                  label: formatMessage('claimWizardPrimaryContact'),
                  step: 3,
                  rows: [
                    [formatMessage('name'), watchedValues.customerName || formatMessage('none')],
                    [formatMessage('phone'), watchedValues.customerPhone || formatMessage('none')],
                    [formatMessage('email'), watchedValues.customerEmail || formatMessage('none')],
                  ],
                },
                {
                  label: formatMessage('vehicle'),
                  step: 3,
                  rows: [
                    [
                      formatMessage('year'),
                      watchedValues?.vehicleDetails?.year || formatMessage('none'),
                    ],
                    [
                      formatMessage('make'),
                      watchedValues?.vehicleDetails?.make || formatMessage('none'),
                    ],
                    [
                      formatMessage('model'),
                      watchedValues?.vehicleDetails?.model || formatMessage('none'),
                    ],
                    [
                      formatMessage('color'),
                      watchedValues?.vehicleDetails?.color || formatMessage('none'),
                    ],
                    [
                      formatMessage('vin'),
                      watchedValues?.vehicleDetails?.vin || formatMessage('none'),
                    ],
                    [
                      formatMessage('plate'),
                      watchedValues?.vehicleDetails?.plate || formatMessage('none'),
                    ],
                    [
                      formatMessage('claimWizardMembershipNumber'),
                      watchedValues?.vehicleDetails?.membershipNumber || formatMessage('none'),
                    ],
                  ],
                },
              ].map((section) => (
                <ClaimWizardReviewSection
                  key={section.label}
                  section={section}
                  editLabel={formatMessage('edit')}
                  onEdit={jumpToClaimWizardStep}
                />
              ))}
            </div>
          );
      }
    };

    return (
      <FormProvider {...methods}>
        <div className={`claim-wizard${props.drawerMode ? ' claim-wizard-drawer-mode' : ''}`}>
          {props.drawerMode && (
            <div className="claim-wizard-drawer-header">
              <DrawerControls
                closeFunction={props.onCloseDrawer}
                maximizeFunction={props.onMaximizeDrawer}
                maximized={props.drawerMaximized}
              />
              <button
                type="button"
                className="button primary claim-wizard-drawer-save"
                onClick={saveClaimForLater}
              >
                {formatMessage('claimWizardSaveFinishLater')}
              </button>
            </div>
          )}
          <main className="claim-wizard-main">
            <section className="claim-wizard-card">
              <Stepper
                className="claim-wizard-steps"
                currentStep={claimWizardStep}
                onStepClick={jumpToClaimWizardStep}
                steps={claimWizardStepLabels}
                ariaLabel={formatMessage('claimWizardProgressAriaLabel')}
              />
              {renderStepContent()}
              {claimWizardError && <div className="claim-wizard-error">{claimWizardError}</div>}
              <div className="claim-wizard-actions">
                {claimWizardStep > 0 && (
                  <button
                    type="button"
                    className="button claim-wizard-back"
                    onClick={() => goToClaimWizardStep(claimWizardStep - 1)}
                  >
                    {formatMessage('back')}
                  </button>
                )}
                {!props.drawerMode && (
                  <button
                    type="button"
                    className="button primary claim-wizard-save"
                    onClick={saveClaimForLater}
                  >
                    {formatMessage('claimWizardSaveFinishLater')}
                  </button>
                )}
                <ButtonWithLoader
                  style="button primary claim-wizard-next"
                  handleClick={
                    claimWizardStep === CLAIM_WIZARD_STEPS.length - 1
                      ? submitClaimWizard
                      : () => goToClaimWizardStep(claimWizardStep + 1)
                  }
                  disabled={props.disabled}
                  isLoading={
                    claimWizardStep === CLAIM_WIZARD_STEPS.length - 1 && props.isSubmitting
                  }
                >
                  <span>
                    {claimWizardStep === CLAIM_WIZARD_STEPS.length - 1
                      ? formatMessage('claimWizardSubmitClaim')
                      : formatMessage('claimWizardContinueToStep', {
                          step: claimWizardStepLabels[claimWizardStep + 1].toLowerCase(),
                        })}
                  </span>
                </ButtonWithLoader>
              </div>
            </section>
          </main>
        </div>
      </FormProvider>
    );
  }

  return null;
};

ClaimWizardForm.propTypes = {
  change: PropTypes.func,
  completeMode: PropTypes.bool,
  drawerMaximized: PropTypes.bool,
  drawerMode: PropTypes.bool,
  fetchCarMakes: PropTypes.func,
  fetchCarModels: PropTypes.func,
  initialValues: PropTypes.shape({}),
  currentUser: PropTypes.shape({}),
  employees: PropTypes.arrayOf(PropTypes.shape({})),
  intl: PropTypes.shape({}).isRequired,
  isSubmitting: PropTypes.bool,
  isNew: PropTypes.bool.isRequired,
  onCloseDrawer: PropTypes.func,
  onMaximizeDrawer: PropTypes.func,
  onSubmit: PropTypes.func.isRequired,
  sites: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
    })
  ),
  type: PropTypes.number,
  carParts: PropTypes.arrayOf(PropTypes.shape({})),
  carMakes: PropTypes.arrayOf(PropTypes.shape({})),
  carModels: PropTypes.shape({}),
};

const mapStateToProps = (state) => {
  return {
    carParts: selectReclamationDamageOptions(state),
    carMakes: selectCarMakes(state),
    carModels: selectCarModels(state),
  };
};

const mapDispatchToProps = {
  fetchCarMakes,
  fetchCarModels,
};
export default compose(
  injectIntl,
  connect(mapStateToProps, mapDispatchToProps),
  withRouter
)(ClaimWizardForm);
